import { apiFetch } from '@/apiFetch';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { describePage } from '../pageContext';
import { useActiveReportTab } from '../activeReportTab';
import { toast } from 'sonner';
import { getListStrategiesQueryKey } from '@biasafe.queries/strategies/strategies';
import { config } from '@/shared/config/env';
import {
  type FocusStrategy,
  useChatSessionStore,
} from '../stores/chatSessionStore';
import { SOTERIA_AGENT, isStrategyCapable } from '../agentKind';
import type { AgentKind } from '../agentKind';
import { describeTurnError } from './turnError';

// ── Public types (consumed by the SoteriaAI components) ────────────
export interface SoteriaToolEvent {
  tool: string;
  /** Human-readable label sent by the backend ("Building 'X' — S&P 500…"). */
  summary?: string;
  result?: string;
  error?: string;
}

/**
 * One step of the strategy-build pipeline, as the checklist draws it.
 *
 * Distinct from SoteriaToolEvent, which is the agent's free-form working trail:
 * a build has a FIXED, known set of steps, published in full before the first
 * one starts. That is the whole point — a trail can only ever say "here is what
 * I have done", where this says "here is the plan, and here is where I am in
 * it", which is what a five-to-eight minute wait needs.
 */
export interface SoteriaBuildStep {
  id: string;
  /** Curated phase name from the backend — never a raw pipeline step id. */
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed';
}

// A file the advisor attached to a turn, as the chat sees it: metadata only.
// Mirrors the API's AttachmentInfo. The bytes live behind
// /soteria/attachments/{id} and are fetched per file when one is displayed, so
// scrolling a long history never drags the attachments through with it.
export interface ChatAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
}

export interface CreatedStrategy {
  strategy_id: string;
  id: string;
  name: string;
  code: string;
  url: string;
}

// The "run backtest" prompt attached to the turn that built a strategy. A build
// never launches a backtest itself, so this is the advisor's one-click way to
// start one once they have seen what was built.
export interface BacktestConfirm {
  strategy_id?: string | null;
  strategy_name?: string | null;
  strategy_code?: string | null;
}

export interface BacktestStarted {
  strategy_id: string;
  strategy_name: string;
  strategy_code: string;
  backtest_id: string;
  backtest_status: string;
  strategy_url: string;
  report_url: string;
}

// One headline metric tile, sourced verbatim from the report's own Key
// Performance Metrics card (so the inline card matches the report exactly).
// Numbers arrive raw (CAGR as 0.057) and are written with the report's own
// per-label rule; text — a date, or a card saved before the tiles carried
// numbers — is shown as stored.
export interface ReportStatTile {
  label: string;
  value?: number | string | null;
  benchmark?: number | string | null;
}

// A "View report" card shown when Soteria reports how a completed backtest did.
// Deep-links to the report tab matching the section it discussed (report_tab)
// and, for the performance section, shows headline metric tiles inline.
export interface BacktestReport {
  strategy_id?: string | null;
  strategy_name?: string | null;
  report_url: string;
  report_tab?: string | null;
  section?: string | null;
  stats?: ReportStatTile[] | null;
}

// A "confirm create client" card the Client Assistant proposes. The advisor
// clicks Create to actually add the prospect (a normal POST /clients). The
// agent never writes — this card only carries the proposed, validated fields.
//
// When `kind` is "confirm_replace", the proposal is a REPLACEMENT: an existing
// prospect (`replaces_id`) shares this email, and since there's no edit-client
// API the advisor's confirm chains DELETE /clients/{replaces_id} → POST /clients
// with the new details.
export interface ClientConfirmCard {
  kind: string; // "confirm_create" | "confirm_replace"
  display_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  residential_country: string; // ISO 3166-1 alpha-3
  client_type: string;
  // Replace-only: the existing prospect this proposal deletes before creating.
  replaces_id?: string;
  replaces_name?: string;
  replaces_reference_code?: string;
}

// One row of a proposed CRM change. `before` is null for something being added.
export interface CrmProposalChange {
  label: string;
  before?: string | null;
  after?: string | null;
}

// A CRM write the Client Assistant PROPOSED and has not made: a timeline entry,
// a follow-up, a change to a client's type/status/priority/source, or applicant
// details to save before the account-opening wizard. Nothing is saved until the
// advisor confirms (POST /soteria/proposals/{id}/confirm); `state` is the
// server's current answer, redrawn on every history load. The card is a list
// item because one turn can propose several.
export interface CrmProposalCard {
  id: string;
  action: string; // log_interaction | create_task | update_client_relationship | update_client_profile
  state: string; // pending | confirming | confirmed | cancelled | failed | expired
  expires_at: string;
  title: string;
  note?: string | null;
  // The change is one the advisor should read twice (e.g. setting CLOSED).
  warning?: boolean;
  client_id: string;
  client_name?: string | null;
  client_reference?: string | null;
  changes: Array<CrmProposalChange>;
}

// An optional joint account holder the assistant gathered, in the wizard's
// Holder shape (identity only — it becomes the second holder).
export interface DraftJointHolder {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
}

// An optional trusted/additional contact the assistant gathered.
export interface DraftContact {
  firstName: string;
  lastName: string;
  email: string;
  relationship: string;
}

// A destination Soteria offers to take the advisor to. `path` is resolved
// server-side from a fixed catalog — never free text from the model.
export interface NavigateCard {
  kind: string; // "navigate"
  destination: string;
  path: string;
  label: string;
}

// An "open account" draft the Client Assistant prepared for an existing client.
// Clicking through opens the account-opening wizard pre-filled at step one; the
// advisor completes KYC/compliance and signs. The agent never opens the account.
export interface AccountDraftCard {
  kind: string; // "open_account"
  client_id: string;
  client_name: string;
  custodian: string; // "IBKR" | "NBIN"
  counts: Record<string, number>; // accountType code → quantity
  account_types: Array<{ code: string; label: string }>;
  // Wizard-shaped "More Details" demographics the assistant gathered.
  details?: { gender?: string; maritalStatus?: string };
  // Optional extras the assistant gathered and handed to the wizard.
  joint_holders?: Array<DraftJointHolder>;
  contacts?: Array<DraftContact>;
  // Applicant details the assistant gathered that the client's record does not
  // hold yet. Saved only when the advisor confirms, which "Review & open" does
  // first; null when the record already has them all.
  profile_proposal?: CrmProposalCard | null;
}

// Strategy-builder "choose one" clarification rendered as clickable buttons.
// One group per field the builder is actually missing (e.g. base universe,
// reconstitution frequency); the label doubles as the value sent on click.
export interface ChoiceOption {
  label: string;
  value: string;
  description?: string;
  /** Whether this option is compatible with what the advisor already said.
   *  `false` is marked, NOT disabled — see ChoiceCard for why. */
  allowed?: boolean;
  /** Why it clashes, in the advisor's terms. Shown beside the option. */
  conflict?: string | null;
}
/** Condition that reveals a follow-up group, evaluated locally against the
 *  answers picked so far — no server turn. */
export interface ChoiceShownWhen {
  field: string;
  values: Array<string>;
}
export interface ChoiceGroup {
  field: string;
  question: string;
  options: Array<ChoiceOption>;
  /** Present on a FOLLOW-UP group: hidden until an earlier group is answered
   *  with one of `values`. Absent on a group that is always asked. */
  shown_when?: ChoiceShownWhen | null;
  /** The value this group was opened to change, offered back once the clash
   *  that opened it is gone. Its options cannot include it — it was illegal
   *  when the group was built. */
  revert_to?: string | null;
}

export interface SoteriaMessage {
  id: string;
  // The SERVER's stable id ("<run_id>:<index>"), present on messages loaded
  // from history. `id` above is minted client-side for optimistic rendering and
  // is regenerated on every load, so it cannot key anything persistent.
  messageId?: string;
  // Thumbs recorded on this message, restored from the server on load.
  rating?: 'up' | 'down';
  role: 'user' | 'assistant';
  content: string;
  // Live "thinking" activity while the reply is pending (e.g. "Scoring &
  // selecting securities…"). Cleared once the final content arrives.
  stage?: string;
  // The strategy-build checklist while this turn is building one, and how long
  // it has been going. Both live only for the duration of the turn — a
  // finished answer is not a progress readout, so they are cleared on
  // completion and never restored from history.
  buildSteps?: Array<SoteriaBuildStep>;
  elapsedSeconds?: number;
  timestamp: Date;
  // Files sent WITH this turn, drawn as chips in the same bubble as the text.
  // Set optimistically when the advisor sends, and restored from the server on
  // history load — an attachment is part of the message, so it has to survive
  // a reload like the words do.
  attachments?: Array<ChatAttachment>;
  tools?: SoteriaToolEvent[];
  strategy?: CreatedStrategy;
  backtest?: BacktestStarted;
  // "Run backtest" prompt on the turn that built a strategy.
  backtest_confirm?: BacktestConfirm;
  // One "View report" card per strategy this turn reported on (multi-strategy
  // turns keep a card for each).
  reports?: Array<BacktestReport>;
  // Client Assistant: a "confirm create client" proposal on this turn.
  client?: ClientConfirmCard;
  // Client Assistant: an "open account" draft handed off to the wizard.
  account_draft?: AccountDraftCard;
  // Client Assistant: CRM writes proposed this turn, each waiting for Confirm.
  crm_proposals?: Array<CrmProposalCard>;
  // Client Assistant / financial: "go to this page" hand-offs. A list — one
  // turn can end on two screens, and the backend drops any destination another
  // card in the same turn already opens.
  navigation?: Array<NavigateCard>;
  // Strategy-builder "choose one" options rendered as clickable buttons.
  choices?: Array<ChoiceGroup>;
}

export interface ChatSummary {
  id: string;
  title: string;
  updatedAt: Date;
  pinned: boolean;
  archived: boolean;
}

// ── API wire shapes (qpm-api /soteria) ─────────────────────────────
interface ServerSession {
  session_id: string;
  title: string;
  created_at: number | null;
  updated_at: number | null;
  pinned?: boolean;
  archived?: boolean;
}
interface ServerMessage {
  role: string;
  content: string;
  // Stable across reloads ("<run_id>:<index>") — what a rating is keyed on.
  message_id?: string | null;
  rating?: string | null;
  strategy?: CreatedStrategy | null;
  backtest?: BacktestStarted | null;
  backtest_confirm?: BacktestConfirm | null;
  reports?: Array<BacktestReport> | null;
  client?: ClientConfirmCard | null;
  account_draft?: AccountDraftCard | null;
  crm_proposals?: Array<CrmProposalCard> | null;
  // A list since the multi-destination fix; a bare object is a turn stored
  // before it (the server wraps those, but tolerate either shape here too).
  navigation?: Array<NavigateCard> | NavigateCard | null;
  choices?: Array<ChoiceGroup> | null;
  // Files the advisor attached to this turn. Metadata only — the bytes are
  // fetched per file from /soteria/attachments/{id} when one is actually shown,
  // so a long history does not carry every attachment it mentions.
  attachments?: Array<ChatAttachment> | null;
}

// Navigation cards arrive as a list; older payloads carry a single object.
// Normalizing here keeps the render path from ever calling .map on an object.
function toNavigationCards(
  value: Array<NavigateCard> | NavigateCard | null | undefined,
): Array<NavigateCard> | undefined {
  if (!value) return undefined;
  const cards = Array.isArray(value) ? value : [value];
  return cards.length > 0 ? cards : undefined;
}

// In staging/prod, `staging.biasafe.ai/v1/*` hits the UI nginx (405); the
// shared config resolves to the cross-origin API host. Local dev falls
// through to the Caddy same-origin proxy at `/v1`.
const API_BASE = config.api.baseUrl ?? '/v1';

// Async-job polling cadence. POST /soteria/query returns a job id
// immediately and runs the agent in the background; we poll the result
// endpoint until it completes.
//
// Exported because useRevealedText paces the typing animation against it: the
// reply can only arrive this often, so the reveal spreads each chunk over
// roughly one interval. Two different numbers there would either stall or race.
// That contract is why the backoff below snaps back to this the moment prose
// starts arriving.
export const POLL_INTERVAL_MS = 1200;

// How long to keep polling before giving up on a turn.
//
// This was an ATTEMPT COUNT of 500 which, at a fixed 1.2s, gave up after ten
// minutes — while the server lets a turn run for thirty
// (SOTERIA_MAX_TURN_SECONDS in job_store.py). A build landing in that
// twenty-minute gap was told "Soteria timed out while generating a response"
// while the job went on to finish perfectly well, and the answer only
// surfaced on a later reload. A measured legitimate turn has taken 12m20s,
// which is squarely inside the gap: this was not a theoretical window.
//
// Wall clock rather than a count, because the interval is no longer fixed.
// Keep this in step with the server cap; the client giving up FIRST is the
// bug, and giving up long after costs nothing but a dead poll.
const MAX_POLL_MS = 30 * 60 * 1000;

// The poll interval, given how long the turn has been going and whether the
// reply has started arriving.
//
// A 1.2s poll for a seven-minute build is ~350 requests for a turn that, until
// prose starts, has nothing to report but a stage label and a step checklist —
// neither of which changes anywhere near that often. Backing off cuts that to
// ~110 without the advisor being able to tell.
//
// The moment there IS prose, it snaps back: the reveal animation is paced
// against POLL_INTERVAL_MS, so a slower poll there would visibly stall the
// typing.
export function pollDelayMs(elapsedMs: number, hasProse: boolean): number {
  if (hasProse) return POLL_INTERVAL_MS;
  // The first stretch stays fast — short turns are the common case, and this
  // is the window where the answer most often lands.
  if (elapsedMs < 20_000) return POLL_INTERVAL_MS;
  if (elapsedMs < 90_000) return 2_500;
  return 5_000;
}
// Keep server data fresh-enough without refetching mid-turn and wiping the
// optimistic message we just added.
const STALE_MS = 30_000;

// How many messages to load per page. The session opens on the newest page
// and each scroll-to-top loads one more page of older messages. Change this
// single value to tune the page size.
export const MESSAGE_PAGE_SIZE = 10;

// Query keys stay namespaced by agent kind. The UI only ever uses `soteria`
// now, so in practice there is one cache — the namespacing is what keeps any
// pre-supervisor per-specialist cache from colliding with it.
const historyKey = (kind: AgentKind) => ['soteria', 'history', kind] as const;
const sessionKey = (kind: AgentKind, id: string) =>
  ['soteria', 'history', kind, id] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The turn in flight, remembered across a page reload.
 *
 * WHY. A turn is submitted, then polled — and the job id lived only in a React
 * ref, so a reload lost it. The job kept running server-side and completed
 * correctly (that is the whole point of the submit-then-poll design), but
 * nothing was left to collect the result: a 12-minute turn was measured getting
 * 27 polls, all of them from before the reload. The advisor saw an idle chat,
 * and the answer only surfaced on a LATER reload, via history.
 *
 * `sessionStorage`, not `localStorage`: per-tab is exactly right here. Two tabs
 * must not both try to adopt the same turn, and a job id has no meaning in a
 * different tab's chat.
 */
const inFlightStorageKey = (kind: AgentKind) => `soteria:inflight:${kind}`;

type InFlightRecord = {
  sessionId: string;
  jobId: string;
  // The question, so the reloaded page can show what is being answered. The
  // optimistic cache that normally holds it does not survive a reload.
  prompt?: string;
};

function rememberInFlight(kind: AgentKind, record: InFlightRecord): void {
  try {
    sessionStorage.setItem(inFlightStorageKey(kind), JSON.stringify(record));
  } catch {
    // Storage disabled or full. Losing the resume is not worth failing a turn.
  }
}

function forgetInFlight(kind: AgentKind): void {
  try {
    sessionStorage.removeItem(inFlightStorageKey(kind));
  } catch {
    // As above.
  }
}

function readInFlight(kind: AgentKind): InFlightRecord | null {
  try {
    const raw = sessionStorage.getItem(inFlightStorageKey(kind));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as InFlightRecord).jobId === 'string' &&
      typeof (parsed as InFlightRecord).sessionId === 'string'
    ) {
      return parsed as InFlightRecord;
    }
  } catch {
    // Malformed (hand-edited, or written by an older build) — treat as absent.
  }
  return null;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// agno stores created_at/updated_at as epoch seconds; tolerate ms too.
function toDate(ts: number | null): Date {
  if (!ts) return new Date(0);
  return new Date(ts < 1e12 ? ts * 1000 : ts);
}

function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  // apiFetch routes a 403 AU403MFAA (no second factor) to /security-setup.
  return apiFetch(path, init);
}

/**
 * The attachment endpoint's own URL, for linking straight at it.
 *
 * ABSOLUTE, from `config.api.baseUrl` — a relative `/v1/...` resolves against
 * the SPA's origin, which in production is not the API's, so the link would
 * reach the static file server and hand back index.html. `apiPaths.test.ts`
 * fails the build over exactly that.
 *
 * Safe to use as an `href` even though the endpoint is authenticated: the
 * session cookie is host-scoped and `SameSite=Lax`, and app and api are
 * subdomains of one site, so a top-level navigation carries it. That is NOT
 * true of `fetchAttachmentUrl` below, which is a subresource fetch and says
 * `credentials: 'include'` to be unambiguous.
 */
export function attachmentUrl(attachmentId: string): string {
  return `${API_BASE}/soteria/attachments/${attachmentId}`;
}

/** An object URL for an attachment's bytes. Caller must revokeObjectURL it.
 *
 * Goes through `authedFetch` rather than pointing an `<img src>` at the endpoint,
 * for two reasons that both bite only in production: the API is a DIFFERENT
 * ORIGIN there (`config.api.baseUrl`, not a relative `/v1` — see
 * shared/config/apiPaths.test.ts), and an `<img>` request carries no explicit
 * credentials, so the session cookie is at the mercy of SameSite. A fetch with
 * `credentials: 'include'` is unambiguous, and it also lets a failure be handled
 * instead of rendering a broken-image icon.
 */
export async function fetchAttachmentUrl(attachmentId: string): Promise<string> {
  const res = await authedFetch(`/soteria/attachments/${attachmentId}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

// Session expired (access cookie TTL). Bounce to /login
// with a return URL so the user lands back on Soteria after re-auth.
function redirectToLogin(): void {
  const returnTo = encodeURIComponent(
    `${window.location.pathname}${window.location.search}`,
  );
  window.location.assign(`/login?returnTo=${returnTo}`);
}

async function fetchHistory(
  kind: AgentKind,
  signal?: AbortSignal,
): Promise<ServerSession[]> {
  const res = await authedFetch(`/soteria/history?agent=${kind}`, { signal });
  if (res.status === 401) {
    redirectToLogin();
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as { sessions?: ServerSession[] };
  return data.sessions ?? [];
}

interface MessagePage {
  messages: SoteriaMessage[];
  total: number;
  hasMore: boolean;
  // Strategies attached to this chat server-side (for the "Focused on" chips),
  // so the focus reappears on reload / another device even without local state.
  focus: Array<FocusStrategy>;
}

async function fetchSessionMessages(
  kind: AgentKind,
  sessionId: string,
  opts: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<MessagePage> {
  const { limit, offset = 0, signal } = opts;
  const qs = new URLSearchParams();
  qs.set('agent', kind);
  if (limit != null) qs.set('limit', String(limit));
  if (offset) qs.set('offset', String(offset));
  const res = await authedFetch(`/soteria/history/${sessionId}?${qs}`, {
    signal,
  });
  // 404 = unknown/empty session (e.g. a draft that was never sent).
  if (res.status === 404)
    return { messages: [], total: 0, hasMore: false, focus: [] };
  if (res.status === 401) {
    redirectToLogin();
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as {
    messages?: ServerMessage[];
    total?: number;
    has_more?: boolean;
    focus?: Array<FocusStrategy>;
  };
  const window = data.messages ?? [];
  const total = data.total ?? window.length;
  // Index ids by distance from the end of history so they stay stable and
  // unique across pages (older pages prepend without colliding).
  const messages: SoteriaMessage[] = window.map((m, i) => ({
    id: `${sessionId}:e${offset + (window.length - 1 - i)}`,
    // Carried through so a rating can target the message the SERVER knows
    // about, not the positional id above (which shifts as pages load).
    messageId: m.message_id ?? undefined,
    rating: (m.rating as 'up' | 'down' | undefined) ?? undefined,
    role: m.role === 'assistant' ? 'assistant' : 'user',
    content: m.content,
    timestamp: new Date(),
    strategy: m.strategy ?? undefined,
    backtest: m.backtest ?? undefined,
    backtest_confirm: m.backtest_confirm ?? undefined,
    reports: m.reports ?? undefined,
    client: m.client ?? undefined,
    account_draft: m.account_draft ?? undefined,
    crm_proposals: m.crm_proposals ?? undefined,
    navigation: toNavigationCards(m.navigation),
    choices: m.choices ?? undefined,
    attachments: m.attachments ?? undefined,
  }));
  return {
    messages,
    total,
    hasMore: data.has_more ?? false,
    focus: data.focus ?? [],
  };
}

/** Ask the server to stop a running turn. Fire-and-forget: the caller's local
 *  teardown is what the user sees, and a failed cancel is no worse than none. */
function cancelOnServer(jobId: string | null): void {
  if (!jobId) return;
  void authedFetch(`/soteria/query/${jobId}/cancel`, {
    method: 'POST',
  }).catch(() => {
    // Best-effort: the run keeps going, exactly as it did before.
  });
}

export function useSoteriaChat(kind: AgentKind = SOTERIA_AGENT) {
  const queryClient = useQueryClient();
  // What the advisor is looking at right now. Sent with every turn so the
  // agent can resolve "this client" / "why did this underperform?" without
  // an extra lookup. Read from the router so it tracks navigation live.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The report tab the advisor is on, so the agent knows which charts are on
  // screen ("explain these plots"). The report keeps its tab in local state, not
  // the URL, so the live signal is what the report publishes; the URL ?tab= is a
  // fallback for a fresh deep-link before the report has mounted.
  const urlReportTab = useRouterState({
    select: (s) => (s.location.search as { tab?: unknown })?.tab,
  });
  const activeSessionId = useChatSessionStore((s) => s.activeByKind[kind]);
  const allDrafts = useChatSessionStore((s) => s.drafts);
  const setActive = useChatSessionStore((s) => s.setActive);
  const removeDraft = useChatSessionStore((s) => s.removeDraft);
  const focusBySession = useChatSessionStore((s) => s.focusBySession);
  const setFocus = useChatSessionStore((s) => s.setFocus);
  // Only this kind's drafts belong in this hook's sidebar/session logic.
  const drafts = useMemo(
    () => allDrafts.filter((d) => d.kind === kind),
    [allDrafts, kind],
  );
  const pruneFocus = useChatSessionStore((s) => s.pruneFocus);

  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  // The job currently being polled, so `stop` can cancel it server-side. A ref
  // rather than state: `stop` must not be re-created (and the composer
  // re-rendered) on every turn, and nothing renders from this.
  const inFlightJobIdRef = useRef<string | null>(null);
  // Set while a reloaded turn is being adopted (see the re-adopt effect), so the
  // waiting animation can be kept bound to the pending bubble even after the
  // history read replaces the messages under it.
  const [adoptingSessionId, setAdoptingSessionId] = useState<string | null>(null);
  const adoptedBubbleIdRef = useRef<string | null>(null);
  // Ends the re-adopt loop below from outside it, for Stop. Null when no
  // reloaded turn is being waited on.
  const adoptCancelRef = useRef<(() => void) | null>(null);

  // Per-session pagination meta. `loaded` counts how many of the `total`
  // historical messages are currently in the cache; `loadingOlder` guards
  // against overlapping scroll-triggered loads.
  const [paging, setPaging] = useState<
    Record<string, { total: number; loaded: number; loadingOlder: boolean }>
  >({});

  const draftIds = useMemo(() => new Set(drafts.map((d) => d.id)), [drafts]);

  // ── Sidebar: server sessions + local drafts (newest first) ──
  const historyQuery = useQuery({
    queryKey: historyKey(kind),
    queryFn: ({ signal }) => fetchHistory(kind, signal),
    staleTime: STALE_MS,
  });

  // Bound the persisted focus map: once the session list has loaded, drop focus
  // entries for sessions that no longer exist. Guarded on `historyQuery.data`
  // being present (the endpoint returns ALL sessions, so a loaded list is
  // authoritative) and always keeps drafts + the active session, so a just-built
  // chat mid-promotion is never pruned.
  useEffect(() => {
    if (!historyQuery.data) return;
    const known = new Set<string>(historyQuery.data.map((s) => s.session_id));
    drafts.forEach((d) => known.add(d.id));
    if (activeSessionId) known.add(activeSessionId);
    const stale = Object.keys(
      useChatSessionStore.getState().focusBySession,
    ).some((id) => !known.has(id));
    if (stale) pruneFocus([...known]);
  }, [historyQuery.data, drafts, activeSessionId, pruneFocus]);

  const chats = useMemo<ChatSummary[]>(() => {
    const draftSummaries: ChatSummary[] = drafts.map((d) => ({
      id: d.id,
      title: 'New chat',
      updatedAt: new Date(d.createdAt),
      pinned: false,
      archived: false,
    }));
    const serverSummaries: ChatSummary[] = (historyQuery.data ?? [])
      .filter((s) => !draftIds.has(s.session_id))
      .map((s) => ({
        id: s.session_id,
        title: s.title || 'Chat',
        updatedAt: toDate(s.updated_at),
        pinned: !!s.pinned,
        archived: !!s.archived,
      }));
    return [...draftSummaries, ...serverSummaries].sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
    );
  }, [drafts, draftIds, historyQuery.data]);

  // ── Messages for the active session ──
  // Drafts have no server row yet, so the query is disabled for them; the
  // optimistic cache (seeded by `send`) still flows through `data`.
  const messagesQuery = useQuery({
    queryKey: sessionKey(kind, activeSessionId ?? '__none__'),
    queryFn: async ({ signal }) => {
      const sid = activeSessionId as string;
      // Open on the newest page; older pages load on scroll-to-top.
      const page = await fetchSessionMessages(kind, sid, {
        limit: MESSAGE_PAGE_SIZE,
        signal,
      });
      // Hydrate the "Focused on" chips from the server's persisted focus, but
      // only when we hold none locally — so reopening a session (or opening it
      // on another device) restores the chips without clobbering a focus the
      // user is actively editing in this browser.
      if (
        page.focus.length &&
        !(useChatSessionStore.getState().focusBySession[sid]?.length ?? 0)
      ) {
        setFocus(sid, page.focus);
      }
      setPaging((p) => ({
        ...p,
        [sid]: {
          total: page.total,
          loaded: page.messages.length,
          loadingOlder: false,
        },
      }));
      return page.messages;
    },
    enabled: !!activeSessionId && !draftIds.has(activeSessionId ?? ''),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
  });

  const messages = messagesQuery.data ?? [];

  const activeMeta = activeSessionId ? paging[activeSessionId] : undefined;
  const hasMoreHistory = activeMeta
    ? activeMeta.loaded < activeMeta.total
    : false;
  const isLoadingOlder = activeMeta?.loadingOlder ?? false;

  // Fetch the next older page and prepend it to the flat message cache,
  // keeping the cache shape the send/stream path relies on. Returns the
  // number of messages prepended so the caller can anchor the scroll.
  const loadOlderMessages = useCallback(async (): Promise<number> => {
    const sid = activeSessionId;
    if (!sid) return 0;
    const meta = paging[sid];
    if (!meta || meta.loadingOlder || meta.loaded >= meta.total) return 0;

    setPaging((p) => ({ ...p, [sid]: { ...p[sid], loadingOlder: true } }));
    try {
      const page = await fetchSessionMessages(kind, sid, {
        limit: MESSAGE_PAGE_SIZE,
        offset: meta.loaded,
      });
      if (page.messages.length) {
        queryClient.setQueryData<SoteriaMessage[]>(
          sessionKey(kind, sid),
          (old) => [...page.messages, ...(old ?? [])],
        );
      }
      setPaging((p) => ({
        ...p,
        [sid]: {
          total: page.total,
          loaded: meta.loaded + page.messages.length,
          loadingOlder: false,
        },
      }));
      return page.messages.length;
    } catch {
      setPaging((p) => ({ ...p, [sid]: { ...p[sid], loadingOlder: false } }));
      return 0;
    }
  }, [activeSessionId, paging, queryClient, kind]);

  const send = useCallback(
    async (
      text: string,
      strategies?: Array<FocusStrategy>,
      // Files already uploaded for this turn (the composer starts the upload as
      // soon as one is chosen and awaits it before calling send, so every id
      // here is a file the server has stored).
      attachments?: Array<ChatAttachment>,
    ) => {
      const trimmed = text.trim();
      // A message that is only a file still says something — "here, look at
      // this" — so an attachment alone is a valid turn.
      if (!trimmed && !attachments?.length) return;

      const superseded = inFlightJobIdRef.current;
      inFlightJobIdRef.current = null;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setError(null);

      // Read the active id at call time — newChat() may have just run
      // synchronously right before this (e.g. the /dashboard hand-off).
      const store = useChatSessionStore.getState();
      const sessionId = store.activeByKind[kind] ?? store.addDraft(kind);

      // A turn still running IN THIS CHAT is superseded by this one, so stop it
      // on the SERVER too. Aborting only our side left the old run going: it
      // finished a pipeline nobody was waiting for, held one of the user's
      // three concurrency slots, and saved an answer that came back on reload.
      // A turn left running in ANOTHER chat (the user switched away mid-answer)
      // is not superseded — it still answers there — so it is left alone.
      const running = readInFlight(kind);
      if (
        superseded &&
        running?.jobId === superseded &&
        running.sessionId === sessionId
      ) {
        cancelOnServer(superseded);
      }

      // Attach any strategies passed with this turn (e.g. from a "Discuss/
      // Compare in Soteria" hand-off) to the chat's focus set, then read the
      // effective focus back so it's sent on this AND every later turn.
      // Focus is a strategy concept: the supervisor and the standalone strategy
      // agent carry one; the client assistant sends none.
      const strategyCapable = isStrategyCapable(kind);
      if (strategyCapable && strategies && strategies.length) {
        store.setFocus(sessionId, strategies);
      }
      const strategyIds = strategyCapable
        ? (useChatSessionStore.getState().focusBySession[sessionId] ?? []).map(
            (f) => f.id,
          )
        : [];
      const wasDraft = useChatSessionStore
        .getState()
        .drafts.some((d) => d.id === sessionId);
      const key = sessionKey(kind, sessionId);

      const userMsg: SoteriaMessage = {
        id: newId('u'),
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
        // Drawn in the bubble immediately, so the file appears in the
        // conversation the moment it is sent rather than after the refetch.
        attachments: attachments?.length ? attachments : undefined,
      };
      const assistantId = newId('a');
      const assistantMsg: SoteriaMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        tools: [],
      };

      // Optimistically show the turn. The backend keeps server-side
      // context per session_id, so we don't replay history each turn.
      queryClient.setQueryData<SoteriaMessage[]>(key, (old) => [
        ...(old ?? []),
        userMsg,
        assistantMsg,
      ]);
      setStreamingId(assistantId);

      try {
        // 1. Submit — returns a job id immediately (202), agent runs in
        //    the background. No long-held connection → scales cleanly.
        const submit = await authedFetch('/soteria/query', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            prompt: trimmed,
            // Route this turn to the right agent (financial vs client).
            agent: kind,
            // What the advisor is looking at, so the agent can resolve
            // "this client" / "this strategy" from the open page. Read the
            // report tab LIVE at send time (getState), not from a render
            // closure — switching tabs doesn't change the URL, so a memoized
            // send would otherwise ship the previous tab. URL ?tab= is the
            // fallback for a fresh deep-link before the report publishes.
            page_context: describePage(
              pathname,
              useActiveReportTab.getState().tab ??
                (typeof urlReportTab === 'string' ? urlReportTab : undefined),
            ),
            // Strategy-capable kinds only: the chat's current focus set,
            // re-sent every turn (empty list = focus cleared). Omitted for the
            // client assistant, which has no focus set.
            ...(strategyCapable ? { strategy_ids: strategyIds } : {}),
            // Files attached to THIS turn. Sent alongside the prompt so the
            // model receives the text and the file as one message. Not re-sent
            // on later turns — the server replays a session's earlier media.
            ...(attachments?.length
              ? { attachment_ids: attachments.map((a) => a.id) }
              : {}),
          }),
          signal: controller.signal,
        });
        if (submit.status === 401) {
          redirectToLogin();
          throw new Error('Session expired: redirecting to login');
        }
        // A full queue is not a fault. One user may have three turns running at
        // once (MAX_INFLIGHT_PER_USER); the fourth used to surface as the raw
        // string "HTTP 429" in the error banner, with the question left sitting
        // in the transcript unanswered — which reads as "Soteria is broken"
        // rather than "wait your turn". The cap was raised 1→3 for exactly this
        // reason; the wording is the other half of that fix.
        if (submit.status === 429) {
          throw new Error(
            'You already have three answers in progress. This one will go ' +
              'through as soon as one of them finishes. Ask again in a moment.',
          );
        }
        if (!submit.ok) throw new Error(`HTTP ${submit.status}`);
        const { job_id: jobId } = (await submit.json()) as { job_id: string };
        // Stop needs to name the job, and it fires from outside this closure.
        inFlightJobIdRef.current = jobId;
        // Also persisted, so a reload can re-adopt this turn — see
        // rememberInFlight.
        rememberInFlight(kind, { sessionId, jobId, prompt: trimmed });

        // 2. Poll the result endpoint until the job finishes.
        const startedAt = Date.now();
        // Whether the reply has started arriving — once it has, the poll goes
        // back to full rate so the reveal animation is not starved.
        let hasProse = false;
        while (true) {
          if (controller.signal.aborted) return;
          await sleep(pollDelayMs(Date.now() - startedAt, hasProse));
          if (controller.signal.aborted) return;

          if (Date.now() - startedAt > MAX_POLL_MS) {
            throw new Error('Soteria timed out while generating a response.');
          }

          const poll = await authedFetch(`/soteria/query/${jobId}/result`, {
            method: 'GET',
            signal: controller.signal,
          });
          if (poll.status === 401) {
            redirectToLogin();
            throw new Error('Session expired: redirecting to login');
          }
          if (poll.status >= 500) {
            throw new Error('Soteria failed to generate a response.');
          }
          if (poll.status === 404) {
            throw new Error('This query is no longer available.');
          }
          if (!poll.ok) throw new Error(`HTTP ${poll.status}`);

          const data = (await poll.json()) as {
            status: string;
            stage?: string | null;
            // The reply so far, while running. Cumulative, not a delta.
            partial?: string | null;
            // The steps taken so far, while running — same shape as
            // `result.tools`, so one renderer covers both.
            tools?: SoteriaToolEvent[] | null;
            // The strategy-build checklist, on a turn that is building one.
            // Declared complete and pending before the first step starts, so
            // "2 of 5" is countable from the very first poll.
            build?: { steps?: Array<SoteriaBuildStep> | null } | null;
            // How long the turn has been running, per the server's own clock.
            elapsed_seconds?: number | null;
            result?: {
              response?: string;
              tools?: Array<{
                tool?: string;
                summary?: string | null;
                result?: string | null;
                error?: string | null;
              }>;
              strategy?: CreatedStrategy | null;
              backtest?: BacktestStarted | null;
              backtest_confirm?: BacktestConfirm | null;
              reports?: Array<BacktestReport> | null;
              client?: ClientConfirmCard | null;
              account_draft?: AccountDraftCard | null;
              crm_proposals?: Array<CrmProposalCard> | null;
              navigation?: Array<NavigateCard> | NavigateCard | null;
              choices?: Array<ChoiceGroup> | null;
            } | null;
          };
          // The server confirms the turn was stopped. Reachable when the run
          // is cancelled from elsewhere (another tab, or a Stop whose local
          // teardown didn't happen) — a local Stop aborts this loop directly.
          // Not an error: drop the pending bubble and leave the transcript as
          // it was, since a cancelled run persists nothing.
          if (data.status === 'cancelled') {
            inFlightJobIdRef.current = null;
            // Drop the pending bubble outright — including any text already
            // streamed into it. A cancelled run persists nothing, so keeping the
            // half-reply would show a message that silently disappears on the
            // next reload. `finally` clears streamingId.
            queryClient.setQueryData<SoteriaMessage[]>(key, (old) =>
              (old ?? []).filter((m) => m.id !== assistantId),
            );
            return;
          }
          if (data.status === 'completed') {
            const responseText = data.result?.response ?? '';
            // Tool steps the agent ran this turn (e.g. "Building strategy").
            const toolEvents: SoteriaToolEvent[] = (
              data.result?.tools ?? []
            ).map((t) => ({
              tool: t.tool ?? 'tool',
              summary: t.summary ?? undefined,
              result: t.result ?? undefined,
              error: t.error ?? undefined,
            }));
            // Optional cards: a strategy created, a backtest launched, one
            // "View report" card per strategy, and/or a client-confirm card.
            const createdStrategy = data.result?.strategy ?? undefined;
            const startedBacktest = data.result?.backtest ?? undefined;
            const backtestConfirm = data.result?.backtest_confirm ?? undefined;
            const reportCards = data.result?.reports ?? undefined;
            const clientCard = data.result?.client ?? undefined;
            const accountDraft = data.result?.account_draft ?? undefined;
            const crmProposals = data.result?.crm_proposals ?? undefined;
            const navigationCards = toNavigationCards(data.result?.navigation);
            const clarificationChoices = data.result?.choices ?? undefined;
            queryClient.setQueryData<SoteriaMessage[]>(key, (old) =>
              (old ?? []).map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content: responseText,
                      stage: undefined,
                      // Progress furniture, cleared with the stage: the answer
                      // has arrived, so a checklist and a running clock beside
                      // it would be describing something that is over.
                      buildSteps: undefined,
                      elapsedSeconds: undefined,
                      tools: toolEvents,
                      strategy: createdStrategy,
                      backtest: startedBacktest,
                      backtest_confirm: backtestConfirm,
                      reports: reportCards,
                      client: clientCard,
                      account_draft: accountDraft,
                      crm_proposals: crmProposals,
                      navigation: navigationCards,
                      choices: clarificationChoices,
                    }
                  : m,
              ),
            );
            // The session now exists server-side under this id. Refresh
            // the sidebar (server-derived title + ordering), then drop
            // the local draft so it doesn't double up.
            // `exact` is REQUIRED: historyKey(kind) is a prefix of every
            // sessionKey (['soteria','history', kind, id]), so a non-exact
            // invalidation would also refetch the active session's messages
            // and wipe the optimistic cards (strategy/backtest/report/client)
            // we just set this turn — they'd flash then vanish.
            await queryClient.invalidateQueries({
              queryKey: historyKey(kind),
              exact: true,
            });
            if (wasDraft) removeDraft(sessionId);
            // A strategy built this turn joins the chat's "Focused on" chips so
            // the chat visibly tracks the strategy it's about (and it's picked
            // up by "compare them" once it has a backtest). De-duped by id.
            if (createdStrategy?.strategy_id) {
              const focusStore = useChatSessionStore.getState();
              const existing = focusStore.focusBySession[sessionId] ?? [];
              if (!existing.some((f) => f.id === createdStrategy.strategy_id)) {
                focusStore.setFocus(sessionId, [
                  ...existing,
                  {
                    id: createdStrategy.strategy_id,
                    name: createdStrategy.name,
                  },
                ]);
              }
            }
            // A turn may have created a strategy / queued a backtest —
            // keep the dashboard's list fresh without a hard refresh.
            void queryClient.invalidateQueries({
              queryKey: getListStrategiesQueryKey(),
            });
            break;
          }

          // The step trail, applied on every running poll and independently of
          // the text below: steps and prose arrive at different moments, and the
          // trail used to appear only when the whole turn finished — so a long
          // build showed one rotating label for minutes and then every step at
          // once. Server-authoritative (the whole list each time), so a dropped
          // poll self-corrects.
          if (data.status === 'running' && data.tools?.length) {
            const tools = data.tools;
            queryClient.setQueryData<SoteriaMessage[]>(key, (old) =>
              (old ?? []).map((m) =>
                m.id === assistantId ? { ...m, tools } : m,
              ),
            );
          }

          // The build checklist and the turn's age, on every running poll.
          // Applied together and independently of the prose below: the
          // checklist is the whole point of a long wait, and it must keep
          // ticking whether or not any text has started arriving. Both are
          // server-authoritative, so a dropped poll self-corrects on the next.
          if (data.status === 'running') {
            const buildSteps = data.build?.steps ?? undefined;
            const elapsedSeconds = data.elapsed_seconds ?? undefined;
            if (buildSteps || elapsedSeconds !== undefined) {
              queryClient.setQueryData<Array<SoteriaMessage>>(key, (old) =>
                (old ?? []).map((m) =>
                  m.id === assistantId
                    ? { ...m, buildSteps, elapsedSeconds }
                    : m,
                ),
              );
            }
          }

          if (data.partial) {
            // Full-rate polling from here on — the reveal animation is paced
            // against POLL_INTERVAL_MS, so a backed-off poll would stall it.
            hasProse = true;
            // The reply is being produced — show it as it arrives rather than a
            // stage label for the whole turn. Once there is prose, it replaces
            // the stage: the two together would have the bubble claim to be
            // "Thinking…" underneath text it has already written.
            //
            // Server-authoritative and idempotent: each poll carries the whole
            // reply so far, so a dropped or out-of-order poll self-corrects on
            // the next one, and `result.response` overwrites it at the end with
            // the scrubbed final text.
            const partial = data.partial;
            queryClient.setQueryData<SoteriaMessage[]>(key, (old) =>
              (old ?? []).map((m) =>
                m.id === assistantId
                  ? { ...m, content: partial, stage: undefined }
                  : m,
              ),
            );
          } else if (data.stage) {
            // No prose yet (still working through tools): reflect the agent's
            // current activity on the pending assistant bubble.
            const stage = data.stage;
            queryClient.setQueryData<SoteriaMessage[]>(key, (old) =>
              (old ?? []).map((m) =>
                m.id === assistantId ? { ...m, stage } : m,
              ),
            );
          }
          // status === 'running' → keep polling.
        }
      } catch (err: unknown) {
        if ((err as { name?: string })?.name === 'AbortError') {
          // Abandoned turn — Stop, an unmount, or a new send superseding this
          // one. Now that the reply streams in, the bubble may already hold
          // half an answer for a run that will persist nothing, so drop it
          // rather than leaving text that vanishes on the next reload.
          queryClient.setQueryData<SoteriaMessage[]>(key, (old) =>
            (old ?? []).filter((m) => m.id !== assistantId),
          );
          return;
        }
        setError(describeTurnError(err));
        // Drop the empty placeholder bubble so a failed turn doesn't
        // leave a blank assistant message behind.
        queryClient.setQueryData<SoteriaMessage[]>(key, (old) =>
          (old ?? []).filter((m) => m.id !== assistantId || m.content),
        );
        // A client timeout doesn't mean the server stopped — the agent's
        // session row exists (and the build may finish later). Promote it from
        // draft to a real sidebar entry so it isn't left as a stale draft that
        // a later "New Chat" could resume.
        if (wasDraft) {
          removeDraft(sessionId);
          void queryClient.invalidateQueries({ queryKey: historyKey(kind) });
        }
      } finally {
        // However this turn ended, it is no longer cancellable. Without this a
        // Stop pressed after the answer arrived would POST a cancel for a
        // finished job — harmless (the server reports already_finished and keeps
        // the result) but a pointless request.
        inFlightJobIdRef.current = null;
        forgetInFlight(kind);
        setStreamingId(null);
      }
    },
    [queryClient, removeDraft, kind, pathname, urlReportTab],
  );

  // ── Re-adopt a turn that was in flight when the page reloaded ──
  //
  // The job runs server-side and finishes on its own; what was missing is
  // anyone waiting for it. So poll the job we remembered until it stops
  // running, then refetch — the answer is already persisted by then, and the
  // ordinary history read renders it, cards and all.
  //
  // Deliberately NOT a resumed stream. Re-attaching the live text would mean
  // rebuilding the pending bubble, its step trail and its word-by-word reveal
  // from a half-finished turn; letting the finished reply arrive through the
  // normal read is a fraction of the moving parts for the outcome that
  // actually matters — the advisor gets their answer without reloading again.
  useEffect(() => {
    const record = readInFlight(kind);
    if (!record) return;
    // This tab submitted the turn itself and is already polling it; adopting it
    // here as well would double the requests and race the two writers.
    if (inFlightJobIdRef.current === record.jobId) return;

    let cancelled = false;
    const controller = new AbortController();
    const cancel = () => {
      cancelled = true;
      controller.abort();
    };
    setAdoptingSessionId(record.sessionId);
    // Stop reads the job to cancel, and how to end the wait, from refs. A
    // re-adopted turn used to set neither, so after a reload Stop sent no
    // cancel, this loop kept polling, and the re-bind effect put back the very
    // bubble Stop had just removed — the spinner never stopped.
    inFlightJobIdRef.current = record.jobId;
    adoptCancelRef.current = cancel;

    // KEEP THE DRAFT until the turn settles, and seed the question back in.
    //
    // A chat is a local draft until its first turn finishes, which matters here
    // because agno writes the session row in its END-OF-RUN upsert — mid-run
    // the server has no row at all. Promoting early therefore deleted the only
    // record of the chat that existed anywhere: gone from the sidebar (no
    // longer a draft, not yet a server session) and no longer openable. That is
    // the reload landing on a brand-new chat with the previous one nowhere in
    // history.
    //
    // So the draft stays, which keeps the chat listed and active, and the
    // remembered prompt is seeded straight back into the cache — a draft's
    // messages come from exactly this cache, since `messagesQuery` is disabled
    // for one. The real transcript replaces it on settle, below.
    const resumedAssistantId = newId('a');
    if (record.prompt) {
      queryClient.setQueryData<SoteriaMessage[]>(
        sessionKey(kind, record.sessionId),
        (old) =>
          old?.length
            ? old
            : [
                {
                  id: newId('u'),
                  role: 'user',
                  content: record.prompt as string,
                  timestamp: new Date(),
                },
                {
                  id: resumedAssistantId,
                  role: 'assistant',
                  content: '',
                  stage: 'Working on it',
                  timestamp: new Date(),
                },
              ],
      );
      // The pending bubble is only ANIMATED while `streamingId` names it — the
      // panel drives every waiting affordance off that (`isStreaming`,
      // `streamingMessageId`, the composer's disabled state). Without this the
      // seeded bubble sat there inert, which read as a turn that had bugged out
      // and been cancelled rather than one still being answered.
      setStreamingId(resumedAssistantId);
    }

    let settled = false;
    const adopt = async (): Promise<void> => {
      try {
        // Same wall-clock ceiling as the live path, and for the same reason:
        // an attempt count stops matching elapsed time once the interval backs
        // off, and a reloaded page is by definition waiting on an ALREADY long
        // turn — the case the old ten-minute ceiling cut short.
        const adoptStartedAt = Date.now();
        while (Date.now() - adoptStartedAt < MAX_POLL_MS) {
          if (cancelled) return;
          const poll = await authedFetch(
            `/soteria/query/${record.jobId}/result`,
            { method: 'GET', signal: controller.signal },
          );
          // 404 = the job expired or never existed; 401 = the session is gone.
          // Either way there is nothing to wait for, and the ordinary auth
          // handling elsewhere covers the redirect.
          if (!poll.ok) return;
          const data = (await poll.json()) as {
            status?: string;
            stage?: string | null;
            build?: { steps?: Array<SoteriaBuildStep> | null } | null;
            elapsed_seconds?: number | null;
          };
          // Keep narrating. The poll already carries the live stage, and this
          // loop used to read only `status` — so after a reload the label froze
          // at the seeded "Working on it" for the rest of the turn, losing the
          // narration exactly when a turn is longest (a 12m build shows one
          // static line). Same treatment as the live path: reflect the stage on
          // whichever bubble is currently pending.
          //
          // The checklist rides along for the same reason, and matters more
          // here than anywhere: a reloaded page has no idea how far in the turn
          // already is, and `elapsed_seconds` is the server's own count, so the
          // clock resumes at the real age rather than restarting at zero.
          if (data.status === 'running') {
            const stage = data.stage ?? undefined;
            const buildSteps = data.build?.steps ?? undefined;
            const elapsedSeconds = data.elapsed_seconds ?? undefined;
            queryClient.setQueryData<Array<SoteriaMessage>>(
              sessionKey(kind, record.sessionId),
              (old) => {
                if (!old?.length) return old;
                const last = old.at(-1);
                if (!last || last.role !== 'assistant' || last.content.trim()) {
                  return old;
                }
                return old.map((m) =>
                  m === last
                    ? {
                        ...m,
                        stage: stage ?? m.stage,
                        buildSteps,
                        elapsedSeconds,
                      }
                    : m,
                );
              },
            );
          }
          if (data.status !== 'running') {
            if (cancelled) return;
            settled = true;
            // Settled — and only NOW does the server have a session row to
            // read. Promote out of draft FIRST: `messagesQuery` is disabled
            // while the id is a draft, so invalidating before this would
            // invalidate a query that never runs. Promoting also unhides the
            // real server row in the sidebar, which carries the actual title
            // instead of the draft's "New chat".
            removeDraft(record.sessionId);
            // REMOVE, not invalidate. Two reasons it has to be this:
            //   * at this instant the query is still disabled (the draft flag
            //     clears on the next render), and `invalidateQueries` only
            //     refetches ACTIVE observers;
            //   * the seeded question above set `dataUpdatedAt` to now, so
            //     against `staleTime` the cache looks FRESH — the observer then
            //     enables, serves the seed, and never fetches.
            // Measured: the turn settled at 14:18:31 and the messages were not
            // fetched until 14:23:41, by a manual second reload. Dropping the
            // entry leaves the newly-enabled query nothing to serve, so it must
            // go to the server.
            queryClient.removeQueries({
              queryKey: sessionKey(kind, record.sessionId),
            });
            void queryClient.invalidateQueries({ queryKey: historyKey(kind) });
            return;
          }
          // Always the backed-off cadence: this loop deliberately does not
          // resume the streamed text (see the note above), so there is no
          // reveal animation to starve — it is only waiting for the turn to
          // settle, and the checklist ticks at that rate perfectly well.
          await sleep(pollDelayMs(Date.now() - adoptStartedAt, false));
        }
      } catch {
        // Aborted on unmount, or the network went away. Nothing to report: the
        // turn is still safely server-side and the next open will read it.
      } finally {
        // Release the refs only if they still name THIS turn: by now Stop may
        // have ended it and a new turn may hold them.
        if (inFlightJobIdRef.current === record.jobId) {
          inFlightJobIdRef.current = null;
        }
        if (adoptCancelRef.current === cancel) adoptCancelRef.current = null;
        // Cleared even when the poll gave up, so a stale id cannot make every
        // future page load poll a job that will never settle.
        if (!cancelled) {
          forgetInFlight(kind);
          setAdoptingSessionId(null);
          // Stop the waiting animation however this ended, or the composer
          // stays disabled and the bubble spins forever. Either bubble may be
          // the live one: the seeded one for a draft, or — once the history read
          // has replaced it — whichever pending row we re-bound to below.
          const boundId = adoptedBubbleIdRef.current;
          adoptedBubbleIdRef.current = null;
          setStreamingId((current) =>
            current === resumedAssistantId || current === boundId
              ? null
              : current,
          );
          // Drop the seeded pending bubble ONLY if we never learned the
          // outcome — an assistant message that spins forever is worse than
          // none. Doing it unconditionally re-created the cache entry that the
          // settle path had just removed, seeding an EMPTY array: the query
          // then believed it held data and never fetched the answer, which is
          // why it took a second reload to appear.
          if (!settled) {
            queryClient.setQueryData<SoteriaMessage[]>(
              sessionKey(kind, record.sessionId),
              (old) =>
                (old ?? []).filter((m) => m.role !== 'assistant' || m.content),
            );
          }
        }
      }
    };

    void adopt();
    return cancel;
    // Mount-only per kind: re-running this on every render would restart the
    // poll, and the record is read once by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  // Keep the waiting animation on the pending bubble for the whole adopted turn.
  //
  // The seeding above only survives for a DRAFT, where `messagesQuery` is
  // disabled. Reload mid-turn in an existing chat and the enabled query resolves
  // and replaces the cache with the server's rows — the user turn, and an
  // assistant turn that is still empty because agno writes its content in the
  // end-of-run upsert. That drops the seeded bubble, leaving `streamingId`
  // naming a message that no longer exists: nothing animated, the composer
  // re-enabled, and the advisor saw an inert blank reply while the turn was
  // still very much running.
  //
  // So re-bind on every message change until the turn settles: adopt whichever
  // contentless assistant row is last, or add one if the transcript ends on the
  // question.
  useEffect(() => {
    if (!adoptingSessionId || adoptingSessionId !== activeSessionId) return;
    const key = sessionKey(kind, adoptingSessionId);
    const current = queryClient.getQueryData<Array<SoteriaMessage>>(key) ?? [];
    const last = current.at(-1);
    if (last?.role === 'assistant' && !last.content.trim()) {
      adoptedBubbleIdRef.current = last.id;
      setStreamingId((prev) => (prev === last.id ? prev : last.id));
      return;
    }
    // Transcript ends on the question (no placeholder row persisted yet) — give
    // the wait something to animate. Appending makes `last` an empty assistant
    // row, so the branch above takes over on the next pass.
    if (last?.role === 'user') {
      const bubbleId = newId('a');
      adoptedBubbleIdRef.current = bubbleId;
      queryClient.setQueryData<Array<SoteriaMessage>>(key, (old) => [
        ...(old ?? []),
        {
          id: bubbleId,
          role: 'assistant',
          content: '',
          stage: 'Working on it',
          timestamp: new Date(),
        },
      ]);
      setStreamingId(bubbleId);
    }
  }, [adoptingSessionId, activeSessionId, messages, kind, queryClient]);

  const newChat = useCallback(() => {
    controllerRef.current?.abort();
    setStreamingId(null);
    setError(null);
    // ALWAYS start a genuinely new session id. We must never reuse an existing
    // draft: a chat that was sent (or whose build timed out client-side) leaves
    // a real server session — reusing its id would resume that session's build
    // state + strategy link (modifying the wrong strategy) and load its
    // history. Prune leftover never-sent empty drafts so repeated clicks don't
    // stack blank rows, but the new chat is always a fresh id.
    const store = useChatSessionStore.getState();
    const stale = store.drafts.filter(
      (d) =>
        d.kind === kind &&
        !(
          queryClient.getQueryData<SoteriaMessage[]>(sessionKey(kind, d.id))
            ?.length ?? 0
        ),
    );
    const newId = store.addDraft(kind);
    for (const d of stale) {
      if (d.id !== newId) store.removeDraft(d.id);
    }
  }, [queryClient, kind]);

  const switchChat = useCallback(
    (id: string) => {
      controllerRef.current?.abort();
      setStreamingId(null);
      setError(null);
      setActive(kind, id);
    },
    [setActive, kind],
  );

  const regenerateLastTurn = useCallback(async () => {
    const sessionId = useChatSessionStore.getState().activeByKind[kind];
    if (!sessionId || draftIds.has(sessionId)) return;

    // Ask the server to hide the last turn FIRST and tell us what was asked.
    // Re-sending without this appends: the user's own question shows up twice
    // and the answer they wanted replaced is still sitting above the new one.
    let prompt = '';
    try {
      const res = await authedFetch(
        `/soteria/history/${sessionId}/regenerate?agent=${kind}`,
        { method: 'POST' },
      );
      if (res.status === 401) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      prompt = ((await res.json()) as { prompt?: string }).prompt ?? '';
    } catch {
      toast.error("Couldn't regenerate that reply. Please try again.");
      return;
    }
    if (!prompt) return;

    // Drop the hidden turn from the view immediately, so the old exchange does
    // not linger while the new one streams in. Done on the cache directly, NOT
    // by refetching: a refetch lands after `send` has drawn the new question and
    // its pending bubble, and replaces them with the server's history, which
    // holds neither yet — the new answer vanished and every poll wrote into a
    // bubble that no longer existed. Cancel any read already in flight for the
    // same reason.
    const key = sessionKey(kind, sessionId);
    await queryClient.cancelQueries({ queryKey: key });
    queryClient.setQueryData<Array<SoteriaMessage>>(key, (old) => {
      const list = old ?? [];
      // The hidden turn is the last question and everything answered after it.
      let lastQuestion = list.length - 1;
      while (lastQuestion >= 0 && list[lastQuestion].role !== 'user') {
        lastQuestion -= 1;
      }
      return lastQuestion === -1 ? list : list.slice(0, lastQuestion);
    });
    await send(prompt);
  }, [draftIds, queryClient, kind, send]);

  const rateMessage = useCallback(
    async (messageId: string, rating: 'up' | 'down') => {
      const sessionId = useChatSessionStore.getState().activeByKind[kind];
      if (!sessionId || draftIds.has(sessionId)) return;

      // Find the message by EITHER id: the client-side one for a message just
      // streamed in, the server one for anything loaded from history.
      //
      // The cache holds the message ARRAY, not the page object: the query's
      // queryFn returns `page.messages` (see the messagesQuery above). Reading
      // it as a MessagePage made `page?.messages` undefined, so `target` was
      // never found, `serverId` was never set, and every rating click returned
      // at the guard below — no request, no thumb, nothing persisted. It failed
      // silently because that guard exists for a legitimate case (a message the
      // server has not seen yet), so there was nothing to notice.
      const cached = queryClient.getQueryData<Array<SoteriaMessage>>(
        sessionKey(kind, sessionId),
      );
      const target = cached?.find(
        (m) => m.id === messageId || m.messageId === messageId,
      );
      const serverId = target?.messageId;
      // A message the server has never seen (still streaming, or a draft) has
      // nothing stable to hang a rating off. Do nothing rather than write one
      // that will not survive the next reload.
      if (!serverId) return;

      // Clicking the same thumb again clears it — a verdict you cannot take
      // back is worse than none.
      const next = target?.rating === rating ? undefined : rating;

      queryClient.setQueryData<Array<SoteriaMessage>>(
        sessionKey(kind, sessionId),
        (old) => old?.map((m) => (m === target ? { ...m, rating: next } : m)),
      );

      try {
        const res = await authedFetch(
          `/soteria/history/${sessionId}/messages/${encodeURIComponent(serverId)}/rating?agent=${kind}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rating: next ?? null }),
          },
        );
        if (res.status === 401) {
          redirectToLogin();
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch {
        // Put the thumb back rather than leaving the UI asserting something
        // the server did not accept.
        queryClient.setQueryData<Array<SoteriaMessage>>(
          sessionKey(kind, sessionId),
          (old) =>
            old?.map((m) =>
              m.messageId === serverId ? { ...m, rating: target?.rating } : m,
            ),
        );
        toast.error("Couldn't save your feedback. Please try again.");
      }
    },
    [draftIds, queryClient, kind],
  );

  const renameChat = useCallback(
    async (id: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      // Drafts have no server row yet — their title is fixed until the
      // first message creates the session.
      if (draftIds.has(id)) return;

      // Optimistically update the sidebar list, then persist.
      queryClient.setQueryData<ServerSession[]>(historyKey(kind), (old) =>
        (old ?? []).map((s) =>
          s.session_id === id ? { ...s, title: trimmed } : s,
        ),
      );
      try {
        const res = await authedFetch(`/soteria/history/${id}?agent=${kind}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: trimmed }),
        });
        if (res.status === 401) {
          redirectToLogin();
          return;
        }
        // Anything else that isn't OK: the invalidate below refetches and the
        // optimistic title snaps back to the old one. Silently, before this —
        // which reads exactly like "renaming is broken" with nothing to act on.
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch {
        toast.error("Couldn't rename this chat. Please try again.");
      } finally {
        void queryClient.invalidateQueries({ queryKey: historyKey(kind) });
      }
    },
    [draftIds, queryClient, kind],
  );

  // Pin / archive are UI flags persisted in the session's session_data.
  // Optimistically patch the sidebar cache, then PATCH the server.
  const patchSessionFlags = useCallback(
    async (id: string, patch: { pinned?: boolean; archived?: boolean }) => {
      if (draftIds.has(id)) return; // drafts have no server row yet
      queryClient.setQueryData<ServerSession[]>(historyKey(kind), (old) =>
        (old ?? []).map((s) => (s.session_id === id ? { ...s, ...patch } : s)),
      );
      try {
        const res = await authedFetch(`/soteria/history/${id}?agent=${kind}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        });
        if (res.status === 401) {
          redirectToLogin();
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch {
        // Name the action that failed — "pin" and "archive" share this path,
        // and an archive that silently un-archives looks like a vanished chat.
        const what = 'archived' in patch ? 'archive' : 'pin';
        toast.error(`Couldn't ${what} this chat. Please try again.`);
      } finally {
        void queryClient.invalidateQueries({ queryKey: historyKey(kind) });
      }
    },
    [draftIds, queryClient, kind],
  );

  const pinChat = useCallback(
    (id: string, pinned: boolean) => patchSessionFlags(id, { pinned }),
    [patchSessionFlags],
  );
  const archiveChat = useCallback(
    (id: string, archived: boolean) => patchSessionFlags(id, { archived }),
    [patchSessionFlags],
  );

  const deleteChat = useCallback(
    async (id: string) => {
      const state = useChatSessionStore.getState();
      const remaining = chats.filter((c) => c.id !== id);
      const nextActive =
        state.activeByKind[kind] === id
          ? (remaining[0]?.id ?? null)
          : state.activeByKind[kind];

      if (draftIds.has(id)) {
        removeDraft(id);
      } else {
        try {
          const res = await authedFetch(
            `/soteria/history/${id}?agent=${kind}`,
            {
              method: 'DELETE',
            },
          );
          if (res.status === 401) {
            redirectToLogin();
            return;
          }
          // 204 = deleted, 404 = already gone — both fine to proceed.
        } catch {
          // Best-effort; still drop it from the local view.
        }
        queryClient.removeQueries({ queryKey: sessionKey(kind, id) });
        void queryClient.invalidateQueries({ queryKey: historyKey(kind) });
      }
      setActive(kind, nextActive);
    },
    [chats, draftIds, removeDraft, setActive, queryClient, kind],
  );

  const stop = useCallback(() => {
    // Tell the SERVER to stop, not just ourselves.
    //
    // Aborting the fetch only ended our polling: the agent run carried on,
    // spending the rest of a multi-minute pipeline, holding one of the user's
    // concurrency slots until its lease lapsed, and persisting cards for a turn
    // they had already walked away from. Pressing Stop and immediately asking
    // something else could therefore 429.
    //
    // Fire-and-forget by design — the button must feel instant, and the local
    // teardown below is what the user actually sees. A failed cancel is no worse
    // than the old behaviour.
    cancelOnServer(inFlightJobIdRef.current);
    inFlightJobIdRef.current = null;
    controllerRef.current?.abort();
    setStreamingId(null);
    // A turn re-adopted after a reload is waited on by its own loop: end it,
    // stop re-binding a pending bubble to it, and forget it so the next load
    // does not adopt a run that was stopped.
    if (adoptCancelRef.current) {
      adoptCancelRef.current();
      adoptCancelRef.current = null;
      adoptedBubbleIdRef.current = null;
      setAdoptingSessionId(null);
      forgetInFlight(kind);
    }
    // Drop the pending bubble the turn was writing into. Its tool chips render
    // a spinner until they carry a result, and a stopped turn never brings one
    // — so Stop used to leave a "Building strategy" chip spinning for ever,
    // which reads as still working. Only a contentless assistant turn goes: an
    // answer that had already arrived is the user's to keep.
    const sid = useChatSessionStore.getState().activeByKind[kind];
    if (sid) {
      queryClient.setQueryData<Array<SoteriaMessage>>(
        sessionKey(kind, sid),
        (old) =>
          (old ?? []).filter(
            (m) => m.role !== 'assistant' || m.content.trim().length > 0,
          ),
      );
    }
  }, [kind, queryClient]);

  // Strategies attached to the active chat (rendered as permanent chips above
  // the composer). Empty for a plain chat.
  const focusStrategies = activeSessionId
    ? (focusBySession[activeSessionId] ?? [])
    : [];

  return {
    messages,
    streamingId,
    error,
    send,
    newChat,
    switchChat,
    renameChat,
    rateMessage,
    regenerateLastTurn,
    pinChat,
    archiveChat,
    deleteChat,
    stop,
    chats,
    // True only on the FIRST history fetch (no cached list yet). Lets the
    // sidebar show a skeleton instead of the "No chats yet" empty state while
    // the list is loading — otherwise a populated history flashes "empty" on
    // every cold open. A cached refetch (staleTime window) keeps this false.
    historyLoading: historyQuery.isLoading,
    // True while an OPENED conversation's first message page is in flight (the
    // query is disabled for drafts, so this stays false for a new chat). Lets
    // the transcript show a skeleton instead of the empty hero flashing when you
    // click into a chat whose messages aren't cached yet.
    messagesLoading: messagesQuery.isLoading,
    activeChatId: activeSessionId,
    hasMoreHistory,
    isLoadingOlder,
    loadOlderMessages,
    focusStrategies,
  };
}
